import { willRedirect } from '@cm-frontend/utils/redirect.utils';
import axios, {
  type AxiosResponse,
  type InternalAxiosRequestConfig
} from 'axios';

import { CodementumUrl } from './codementum-url';
import { CookieManager } from './cookie-manager';

export const instance = axios.create({
  baseURL: CodementumUrl.api(),
  timeout: 20000
});

instance.interceptors.request.use(
  function (config: InternalAxiosRequestConfig) {
    config.headers.Authorization = CookieManager.getTokenString();
    return config;
  },
  function (error: unknown) {
    const e = error as { errorCode?: number; message?: string };
    const { errorCode, message } = e;

    if (errorCode === 1200) {
      CookieManager.remove(CookieManager.AUTH_COOKIE_NAME);
    }

    if (
      errorCode === 1200 &&
      ['production', 'staging'].includes(CodementumUrl.buildMode)
    ) {
      const panelLoginUrl = new URL(CodementumUrl.panel('login'));

      const { redirect, url } = willRedirect(panelLoginUrl);

      if (redirect) {
        window.location.href = url.toString();
      }
    }

    return Promise.reject(message);
  }
);

instance.interceptors.response.use(function (response: AxiosResponse) {
  return response.data;
});

export function getUserMe() {
  return instance.get('/panel/user/me');
}

export function getUserScopeMe() {
  return instance.get(
    `/panel/scope/me?search=active==true and expiresAt=ge=${new Date().toISOString()}`
  );
}

export function logout() {
  return instance.post('/panel/user/logout');
}

export function getUserScopeProducts(userUuid: string) {
  return instance.get(`/panel/scope/${userUuid}/products`);
}

export type LoginHistory = {
  id: number;
  ipAddress: string;
  browser: string;
  os: string;
  ipApiCountry: string;
  ipApiRegion: string;
  loggedInAt: string;
};

export function getLoginHistory(page: number, count: number) {
  type Resp = {
    list: LoginHistory[];
    page: number;
    count: number;
    totalCount: number;
  };
  return instance.get<Resp>('/panel/user/loginHistory/me', {
    params: { sort: 'loggedInAt,DESC', page, count }
  });
}

export function deleteLoginHistoryEntry(
  id: number,
  headers?: Record<string, string>
) {
  return instance.delete(`/panel/user/loginHistory/${id}`, { headers });
}

export function getAdminLoginHistory(
  page: number,
  count: number,
  userUuid: string | null
) {
  const search = userUuid ? 'user.uuid==' + userUuid : undefined;
  return instance.get('/panel/user/loginHistory', {
    params: {
      sort: 'loggedInAt,DESC',
      page,
      count,
      ...(search ? { search } : {})
    }
  });
}

export function getUserDetilsList({
  uuid = '',
  searchText = '',
  searchFilterSuffix = ''
}) {
  let search = '';
  if (searchFilterSuffix) {
    search += `${searchFilterSuffix} `;
  }
  if (searchText) {
    search += `(firstName==${searchText}* or lastName==${searchText}* or username==${searchText}* or email==${searchText}*)`;
  }
  if (uuid) {
    search += `uuid==${uuid}`;
  }
  return instance.get('/panel/user', {
    params: {
      count: 20,
      search
    }
  });
}

export function getUserDetailsByUuid(uuid: string) {
  return instance.get(`/panel/user/${uuid}`);
}
